import { useQuery } from "@apollo/client";

import { getFragmentData } from "../../__generated__/fragment-masking";
import type {
  CreditAccountFieldsFragment,
  WorkerDepartmentEnum,
} from "../../__generated__/graphql";
import {
  ClientCreditAccountsDocument,
  CreditAccountFieldsFragmentDoc,
  CreditTypeEnum,
  PackageDepartmentEnum,
} from "../../__generated__/graphql";
import { useClientId } from "../../shared";

export type CreditAccount = CreditAccountFieldsFragment;

export type DepartmentCreditKey = "massage" | "beauty" | "petCare";

const DEPARTMENT_CREDIT_NAMES: Record<DepartmentCreditKey, PackageDepartmentEnum> = {
  massage: PackageDepartmentEnum.Massage,
  beauty: PackageDepartmentEnum.HomeBeauty,
  petCare: PackageDepartmentEnum.PetCare,
};

function findDepartmentCredit(
  accounts: CreditAccount[],
  departmentName: PackageDepartmentEnum,
): CreditAccount | undefined {
  return accounts.find(
    (account) =>
      account.creditType === CreditTypeEnum.Department &&
      account.linkedObject?.__typename === "Department" &&
      account.linkedObject.name === departmentName,
  );
}

export function useClientCreditAccounts(
  departments?: WorkerDepartmentEnum[] | WorkerDepartmentEnum,
) {
  const clientId = useClientId();
  const { data, loading, error, refetch } = useQuery(ClientCreditAccountsDocument, {
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: { clientId, departments },
  });

  const creditAccounts = (data?.client.creditAccounts ?? []).map((account) =>
    getFragmentData(CreditAccountFieldsFragmentDoc, account),
  );

  const mainCredit = creditAccounts.find(
    (account) => account.creditType === CreditTypeEnum.General,
  );

  const departmentCredits = {
    massage: findDepartmentCredit(creditAccounts, DEPARTMENT_CREDIT_NAMES.massage),
    beauty: findDepartmentCredit(creditAccounts, DEPARTMENT_CREDIT_NAMES.beauty),
    petCare: findDepartmentCredit(creditAccounts, DEPARTMENT_CREDIT_NAMES.petCare),
  } satisfies Record<DepartmentCreditKey, CreditAccount | undefined>;

  // Total balance spans the general wallet plus every department wallet, mirroring the
  // "Total Balance" figure on the account credit page.
  const totalBalance = creditAccounts
    .filter(
      (account) =>
        account.creditType === CreditTypeEnum.General ||
        account.creditType === CreditTypeEnum.Department,
    )
    .reduce((sum, account) => sum + (account.balance ?? 0), 0);

  return {
    creditAccounts,
    mainCredit,
    departmentCredits,
    totalBalance,
    loading,
    error,
    refetch,
  };
}
